import { API_BASE_URL, apiFetch } from '@/lib/api/http';

export type AttendancePart = 'main' | 'social';
export type AttendanceStatus = 'onsite' | 'online' | 'late' | 'absent' | 'unanswered';

export const ATTENDANCE_PART_LABELS: Record<AttendancePart, string> = {
  main: '本編',
  social: '交流会',
};

/** 出欠がどこから決まったか。画面に生値（self 等）を出さないための対応表（D-083） */
export const ATTENDANCE_SOURCE_LABELS: Record<'self' | 'meet_log' | 'manual', string> = {
  self: '本人の回答',
  meet_log: 'Meet参加ログ',
  manual: '事務局が変更',
};

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  onsite: '現地',
  online: 'オンライン',
  // 入室が開始から遅れていた場合に、Meet参加ログの取込で自動的に付く（D-117）
  late: '遅刻',
  absent: '欠席',
  unanswered: '未回答',
};

export interface Attendance {
  id: string;
  event_id: string;
  member_id: string;
  member_name: string | null;
  part: AttendancePart;
  status: AttendanceStatus;
  source: 'self' | 'meet_log' | 'manual';
  decided_at: string | null;
  note: string | null;
  waitlisted: boolean;
}

export function fetchAttendance(eventId: string): Promise<Attendance[]> {
  return apiFetch<Attendance[]>(`/events/${eventId}/attendance`);
}

/** 出欠一覧CSV（事務局）。手元の席次表などの元データ用 / D-284 */
export function attendanceExportUrl(eventId: string): string {
  return `${API_BASE_URL}/events/${eventId}/attendance/export`;
}

export function selfUpdateAttendance(
  eventId: string,
  part: AttendancePart,
  status: 'onsite' | 'online' | 'absent',
): Promise<Attendance> {
  return apiFetch<Attendance>(`/events/${eventId}/attendance/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ part, status }),
  });
}

export function manualUpdateAttendance(
  eventId: string,
  memberId: string,
  part: AttendancePart,
  status: AttendanceStatus,
  note?: string,
): Promise<Attendance> {
  return apiFetch<Attendance>(`/events/${eventId}/attendance/${memberId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ part, status, note: note ?? null }),
  });
}

// --- 欠席申請（指示書 §4） ---------------------------------------------------
export type AbsenceRequestStatus = 'pending' | 'approved' | 'rejected';

export interface AbsenceHistoryEntry {
  status: string;
  reason: string;
  at: string;
  decided_by?: string;
}

export interface AbsenceRequest {
  id: string;
  event_id: string;
  member_id: string;
  member_name: string | null;
  reason: string;
  status: AbsenceRequestStatus;
  decided_by: string | null;
  decided_at: string | null;
  history: AbsenceHistoryEntry[];
  created_at: string;
}

/** 事務局の判断理由。差戻し・承認とも history の最後の該当行（D-230） */
export function absenceDecisionReason(request: AbsenceRequest): string | null {
  if (request.status !== 'approved' && request.status !== 'rejected') return null;
  for (let i = request.history.length - 1; i >= 0; i -= 1) {
    const entry = request.history[i];
    if (!entry) continue;
    if (entry.status === request.status && entry.reason) return entry.reason;
  }
  return null;
}

export function fetchAbsenceRequests(eventId: string): Promise<AbsenceRequest[]> {
  return apiFetch<AbsenceRequest[]>(`/events/${eventId}/absence-requests`);
}

export function createAbsenceRequest(eventId: string, reason: string): Promise<AbsenceRequest> {
  return apiFetch<AbsenceRequest>(`/events/${eventId}/absence-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export function decideAbsenceRequest(
  requestId: string,
  status: 'approved' | 'rejected',
  reason: string,
): Promise<AbsenceRequest> {
  return apiFetch<AbsenceRequest>(`/absence-requests/${requestId}/decision`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, reason }),
  });
}
